import {LazyLoadingCacheService} from './services/lazy-loading-cache.service';
import {SearchFiltersService} from './services/search-filters.service';
import {ManageBreadcrumbService} from './services/manage-breadcrumb.service';
import {Router, RouteReuseStrategy, RouterModule} from '@angular/router';
import {CommonPipesModule} from './pipes/common-pipes.module';
import {UtilsService} from './services/utils.service';
import {UrlHelperService} from './services/url-helper.service';
import {CommonTokenStorage} from './services/common-token-storage.service';
import {SortDirective} from './services/sortable.directive';
import {ParserService} from './services/parser.service';
import {NumbersOnlyDirective} from './directives/numbers-only/numbers-only.directive';
import {AlphabetsOnlyDirective} from './directives/alphabets-only/alphabets-only.directive';
import {VersionNumberDirective} from './directives/version-number/version-number.directive';
import {MakeCallService} from './services/makecall.service';
import {GeneralEventService} from './services/generalEvents.service';
import {DataStorageService} from './services/data-storage.service';
import {CustomValidatorsService} from './services/custom-validators.service';
import {CardValidator} from './services/card-validator.service';
import {AlertService} from './services/alert.service';
import {LoaderService} from './services/loader/loader.service';
import {UploadService} from './services/upload.service';
import {LoaderComponent} from './components/loader/loader.component';
import {MaterialModule} from './material/material.module';
import {
  CUSTOM_ELEMENTS_SCHEMA, Inject,
  ModuleWithProviders,
  NgModule, Optional,
  APP_INITIALIZER, Injector
} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BreadcrumbModule} from 'xng-breadcrumb';
import {SwiperModule} from 'swiper/angular';
import {PageNotFoundComponent} from './components/page-not-found/page-not-found.component';
import {NgbDateFooParserFormatterService} from './services/ngb-date-foo-parser-formatter.service';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {EditorModule} from '@tinymce/tinymce-angular';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {NoteLimitService} from './services/note-limit.service';
import {LazyLoadedComponent} from './components/lazy-loaded/lazy-loaded.component';
import {FooRouteReuseStrategy} from './core/foo-route-reuse-strategy';
import {FormValidationDirective} from './directives/form-validation/form-validation.directive';
import {FormValidationContainerDirective} from './directives/form-validation/form-validation-container.directive';
import {OnClickedOutsideDirective} from './directives/on-clicked-outside/on-clicked-outside.directive';
import {NgSelectCustomDirective} from './directives/ng-select-custom/ng-select-custom.directive';
import {
  APP_GENERAL_CONFIG,
  CommonAppGeneralConfig,
  CommonGeneralConfigService
} from './services/common-general-config.service';
import {PopupTinymceEditorDirective} from './directives/popup-tinymce-editor/popup-tinymce-editor.directive';

import {MAT_DIALOG_DEFAULT_OPTIONS} from '@angular/material/dialog';
import {
  PopupsDropdownsAlignmentDirective
} from './directives/popups-dropdowns-alignment/popups-dropdowns-alignment.directive';
import {InputUnitDirective} from './directives/input-unit/input-unit.directive';
import {ReplaceSeparatorAndCapitalizePipe} from '../lib/pipes/replace-separator-and-capitalize.pipe';
import {ColorService} from './services/color.service';
import {DomainService} from './services/domain.service';
import {StringobjectPipe} from '../lib/pipes/stringobject.pipe';
import {
  AllowedCharactersDirective
} from './directives/allowed-characters/allowed-characters.directive';
import {appInitializerFactory} from './appInitializerFactory';
import {WhitespacesDirective} from './directives/whitespaces-characters/whitespaces.directive';

import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module} from 'ng-recaptcha';
import {RecaptchaService} from "./services/recaptcha.service"
import {CurrencyService} from './services/currencies/currency.service';
import {FormatNumberBasedOnCurrencyPipe} from '../lib/pipes/format-number-based-on-currency.pipe';
import {TinymceConfigService} from './services/tinymce-config.service';
import {provideHttpClient, withJsonpSupport} from '@angular/common/http';
import {MapService} from "./services/map.service";
import {TranslationService} from "./services/translation.service";
import {TranslationManagerService} from "./services/translation-manager/translation-manager.service";
import { APP_REPLACEABLE_CONFIG, ReplaceableComponentConfig, ReplaceableConfigService } from './services/replaceable-config.service';
import { DisableReplacementDirective } from './directives/replaceable';
import {SessionRefreshService} from "./services/session-refresh.service";
import { PhpConfigService } from './services/php-config.service';
import { PARENT_ROUTES, ParentRoutes, RouteService } from './services/router/route.service';
import { FooRouterService } from './services/router/foo-router.service';

const components: any[] = [
  LoaderComponent,
  PageNotFoundComponent,
  SortDirective,
  NoteLimitService,
  LazyLoadedComponent
];
const directives: any[] = [
  NgSelectCustomDirective,
  AlphabetsOnlyDirective,
  NumbersOnlyDirective,
  SortDirective,
  OnClickedOutsideDirective,
  FormValidationDirective,
  FormValidationContainerDirective,
  PopupTinymceEditorDirective,
  PopupsDropdownsAlignmentDirective,
  InputUnitDirective,
  VersionNumberDirective,
  AllowedCharactersDirective,
  WhitespacesDirective,
  DisableReplacementDirective
];
const services: any[] = [
  {
    provide: APP_INITIALIZER,
    useFactory: appInitializerFactory,
    deps: [TranslateService, Injector, CommonTokenStorage],
    multi: true
  },
  SearchFiltersService,
  DomainService,
  RecaptchaService,
  {
    provide: RouteReuseStrategy,
    useClass: FooRouteReuseStrategy,
  },
  {
    provide: NgbDateParserFormatter,
    useClass: NgbDateFooParserFormatterService,
  },
  {provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {disableClose: true, hasBackdrop: true, autoFocus: false}},
  ReplaceSeparatorAndCapitalizePipe,
  TinymceConfigService,
  PhpConfigService,
  StringobjectPipe,
  {
    provide: RECAPTCHA_V3_SITE_KEY,
    useFactory: () => {
      if (window['google_recaptcha']) {
        return window['google_recaptcha_site_key'];
      } else {
        return null;
      }
    }
  },
  CurrencyService,
  FormatNumberBasedOnCurrencyPipe,
  provideHttpClient(withJsonpSupport()),
  MapService,
  AlertService,
  LoaderService,
  CardValidator,
  CustomValidatorsService,
  DataStorageService,
  GeneralEventService,
  LazyLoadingCacheService,
  MakeCallService,
  ManageBreadcrumbService,
  ColorService,
  {
    provide: NgbDateParserFormatter,
    useClass: NgbDateFooParserFormatterService,
  },
  ParserService,
  CommonGeneralConfigService,
  CommonTokenStorage,
  UploadService,
  SessionRefreshService,
  UrlHelperService,
  UtilsService,
  TranslationService,
  TranslationManagerService,
  {
    provide: APP_INITIALIZER,
    useFactory: (manager: TranslationManagerService) => {
      return () => manager.initTranslations();
    },
    deps: [TranslationManagerService],
    multi: true
  }
];

@NgModule({
  declarations: [
    ...components,
    ...directives
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [
    ...components,
    ...directives,
    TranslateModule,
    CommonModule,
    MaterialModule,
    CommonPipesModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    SwiperModule,
    BreadcrumbModule,
    EditorModule,
    DragDropModule,
    RecaptchaV3Module
  ],
  imports: [
    TranslateModule,
    CommonModule,
    MaterialModule,
    CommonPipesModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    SwiperModule,
    BreadcrumbModule,
    EditorModule,
    DragDropModule,
    RecaptchaV3Module
  ]
})
export class FooCommonModule {
  static forRoot(
    config: CommonAppGeneralConfig = {},
    componentsConfig?: ReplaceableComponentConfig[],
    parentRoutes?: ParentRoutes | ParentRoutes[]
  ): ModuleWithProviders<FooCommonModule> {
    return {
      ngModule: FooCommonModule,
      providers: [
        ...services,
        {provide: APP_GENERAL_CONFIG, useValue: config, multi: true},
        {provide: APP_REPLACEABLE_CONFIG, useValue: componentsConfig, multi: true},
        {provide: PARENT_ROUTES, useValue: parentRoutes, multi: true},
        {provide: Router, useClass: FooRouterService}
      ],
    };
  }


  static forChild(
    parentRoutes?: ParentRoutes | ParentRoutes[]
  ): ModuleWithProviders<FooCommonModule> {
    return {
      ngModule: FooCommonModule,
      providers: [
        {provide: PARENT_ROUTES, useValue: parentRoutes, multi: true},
      ],
    };
  }

  
  constructor(
    generalConfig: CommonGeneralConfigService,
    @Optional() @Inject(APP_GENERAL_CONFIG) configs: CommonAppGeneralConfig[],
    replaceableConfig: ReplaceableConfigService,
    @Optional() @Inject(APP_REPLACEABLE_CONFIG) componentsConfig: ReplaceableComponentConfig[],
    routeService: RouteService,
    @Optional() @Inject(PARENT_ROUTES) parentRoutes: ParentRoutes[] | ParentRoutes,
  ) {
    if (configs) {
      configs.forEach((config: CommonAppGeneralConfig) => {
        generalConfig.setConfig(config);
      });
    }

    if (componentsConfig) {
      replaceableConfig.setComponentsConfig(componentsConfig);
    }

    if (parentRoutes) {
      routeService.setParentRoutes(parentRoutes);
    }

  }
}
